/**
 * PersonaSelector Component
 * Preset persona secimi - Expert, Baby, Mentor, Master, Custom
 */

import { Brain, GraduationCap, Settings, Sparkles, Wand2 } from 'lucide-react';
import { PERSONA_LIST, type AgentPersona } from '../../data/agentPersonas';

interface PersonaSelectorProps {
  selectedPersonaId: string;
  onSelect: (persona: AgentPersona) => void;
}

const PERSONA_ICONS = {
  expert: Brain,
  baby: Sparkles,
  mentor: GraduationCap,
  master: Wand2,
  custom: Settings
};

const PERSONA_COLORS = {
  expert: '#3b82f6',
  baby: '#22c55e',
  mentor: '#ec4899',
  master: '#8b5cf6',
  custom: '#64748b'
};

export default function PersonaSelector({ selectedPersonaId, onSelect }: PersonaSelectorProps) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
      {PERSONA_LIST.map(persona => {
        const Icon = PERSONA_ICONS[persona.id as keyof typeof PERSONA_ICONS];
        const color = PERSONA_COLORS[persona.id as keyof typeof PERSONA_COLORS];
        const isSelected = selectedPersonaId === persona.id;

        return (
          <div
            key={persona.id}
            onClick={() => onSelect(persona)}
            style={{
              padding: '24px',
              borderRadius: '12px',
              border: isSelected ? `2px solid ${color}` : '2px solid #e2e8f0',
              background: isSelected ? `${color}08` : '#fff',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              position: 'relative'
            }}
            onMouseEnter={(e) => {
              if (!isSelected) {
                e.currentTarget.style.borderColor = color;
                e.currentTarget.style.transform = 'translateY(-2px)';
              }
            }}
            onMouseLeave={(e) => {
              if (!isSelected) {
                e.currentTarget.style.borderColor = '#e2e8f0';
                e.currentTarget.style.transform = 'translateY(0)';
              }
            }}
          >
            {/* Selected Indicator */}
            {isSelected && (
              <div style={{
                position: 'absolute',
                top: '12px',
                right: '12px',
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                background: color,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                fontSize: '14px',
                fontWeight: '700'
              }}>✓</div>
            )}

            {/* Icon */}
            <div style={{
              width: '56px',
              height: '56px',
              borderRadius: '50%',
              background: `linear-gradient(135deg, ${color}, ${color}dd)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: '16px'
            }}>
              <Icon size={28} color="#fff" />
            </div>

            {/* Name & Type */}
            <div style={{ marginBottom: '12px' }}>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '700',
                color: '#1a202c',
                marginBottom: '4px'
              }}>{persona.name}</h3>
              <span style={{
                fontSize: '12px',
                fontWeight: '600',
                padding: '4px 10px',
                borderRadius: '12px',
                background: `${color}15`,
                color: color
              }}>{persona.icon} {persona.name.toUpperCase()}</span>
            </div>

            {/* Description */}
            <p style={{
              fontSize: '14px',
              color: '#64748b',
              lineHeight: '1.6',
              marginBottom: '16px'
            }}>{persona.description}</p>

            {/* Personality Preview */}
            <div style={{
              padding: '12px',
              borderRadius: '8px',
              background: '#f8fafc',
              marginBottom: '12px'
            }}>
              <div style={{ fontSize: '12px', fontWeight: '600', color: '#64748b', marginBottom: '8px' }}>
                Kisilik Ozellikleri:
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                {Object.entries(persona.personalityTraits).map(([key, value]) => (
                  <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '11px', color: '#94a3b8', width: '80px', textTransform: 'capitalize' }}>
                      {key}:
                    </span>
                    <div style={{
                      flex: 1,
                      height: '4px',
                      borderRadius: '2px',
                      background: '#e2e8f0',
                      position: 'relative',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        height: '100%',
                        width: `${value}%`,
                        background: color,
                        borderRadius: '2px'
                      }} />
                    </div>
                    <span style={{ fontSize: '11px', color: '#64748b', width: '30px', textAlign: 'right' }}>
                      {String(value)}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Example Response */}
            <div style={{
              padding: '12px',
              borderRadius: '8px',
              background: '#f8fafc',
              border: '1px solid #e2e8f0'
            }}>
              <div style={{ fontSize: '11px', fontWeight: '600', color: '#64748b', marginBottom: '6px' }}>
                Ornek Yanit:
              </div>
              <p style={{
                fontSize: '12px',
                color: '#1a202c',
                lineHeight: '1.5',
                fontStyle: 'italic'
              }}>&quot;{persona.exampleBehavior[0]}&quot;</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}
